import { dayKey, dayKeyToMs, escapeHtml, formatCount, formatDate, formatDateWithWeekday, wallClock } from '../format';
import type { DayCount } from '../types';

/**
 * Calendar heatmap for the latest 53 weeks (Sunday-first columns), built from
 * ChatStats.calendar (active days only). Shorter chats start at the week of
 * their first message instead of showing months of empty squares.
 * Pure model + HTML markup.
 */

export const HEATMAP_WEEKS = 53;
const DAY_MS = 86_400_000;

export type HeatLevel = 0 | 1 | 2 | 3 | 4;

export interface HeatmapCell {
  readonly date: string;
  readonly count: number;
  readonly level: HeatLevel;
  /** Between the first and the last message day of the chat. */
  readonly inRange: boolean;
  /** After the last message day (end of the last column). */
  readonly future: boolean;
}

export interface HeatmapMonth {
  /** YYYY-MM */
  readonly key: string;
  readonly label: string;
  readonly messages: number;
  readonly activeDays: number;
}

export interface HeatmapModel {
  /** Up to HEATMAP_WEEKS columns of 7 cells, Sunday first. */
  readonly weeks: readonly (readonly HeatmapCell[])[];
  /** Month label positioned at the column holding the 1st of that month. */
  readonly monthLabels: readonly { readonly week: number; readonly label: string }[];
  /** Upper bounds of levels 1-3 (level 4 is above the last one). */
  readonly thresholds: readonly [number, number, number];
  /** First and last visible days that belong to the chat. */
  readonly from: string;
  readonly to: string;
  readonly activeDays: number;
  readonly messages: number;
  readonly busiest: DayCount | null;
  /** Months inside the window, oldest first (text alternative). */
  readonly months: readonly HeatmapMonth[];
}

function quantile(sorted: readonly number[], q: number): number {
  if (sorted.length === 0) return 0;
  const position = (sorted.length - 1) * q;
  const lower = Math.floor(position);
  const upper = Math.ceil(position);
  const value = sorted[lower]! + (sorted[upper]! - sorted[lower]!) * (position - lower);
  return Math.round(value * 100) / 100;
}

export function heatLevel(count: number, thresholds: readonly [number, number, number]): HeatLevel {
  if (count <= 0) return 0;
  if (count <= thresholds[0]) return 1;
  if (count <= thresholds[1]) return 2;
  if (count <= thresholds[2]) return 3;
  return 4;
}

export function buildHeatmap(calendar: readonly DayCount[], firstAt: number, lastAt: number): HeatmapModel {
  const endMs = dayKeyToMs(dayKey(lastAt));
  const startOfChat = dayKeyToMs(dayKey(firstAt));
  const lastColumnStart = endMs - wallClock(endMs).weekday * DAY_MS;
  const chatColumnStart = Math.min(lastColumnStart, startOfChat - wallClock(startOfChat).weekday * DAY_MS);
  const firstColumnStart = Math.max(lastColumnStart - (HEATMAP_WEEKS - 1) * 7 * DAY_MS, chatColumnStart);
  const weekCount = Math.round((lastColumnStart - firstColumnStart) / (7 * DAY_MS)) + 1;
  const windowStart = Math.max(firstColumnStart, startOfChat);

  const counts = new Map<string, number>();
  for (const day of calendar) counts.set(day.date, day.count);

  const inWindow = calendar.filter((day) => {
    const ms = dayKeyToMs(day.date);
    return ms >= windowStart && ms <= endMs && day.count > 0;
  });
  const sorted = inWindow.map((day) => day.count).sort((a, b) => a - b);
  const thresholds: [number, number, number] = [quantile(sorted, 0.25), quantile(sorted, 0.5), quantile(sorted, 0.75)];

  const weeks: HeatmapCell[][] = [];
  const monthLabels: { week: number; label: string }[] = [];
  for (let week = 0; week < weekCount; week++) {
    const column: HeatmapCell[] = [];
    for (let weekday = 0; weekday < 7; weekday++) {
      const ms = firstColumnStart + (week * 7 + weekday) * DAY_MS;
      const date = dayKey(ms);
      const future = ms > endMs;
      const inRange = !future && ms >= startOfChat;
      const count = inRange ? counts.get(date) ?? 0 : 0;
      column.push({ date, count, level: heatLevel(count, thresholds), inRange, future });
      const clock = wallClock(ms);
      if (clock.day === 1 && week > 0 && !future) monthLabels.push({ week, label: `${clock.month}월` });
    }
    weeks.push(column);
  }
  if (!monthLabels.length || monthLabels[0]!.week > 2) {
    monthLabels.unshift({ week: 0, label: `${wallClock(firstColumnStart + 6 * DAY_MS).month}월` });
  }

  const months = new Map<string, { messages: number; activeDays: number }>();
  for (let ms = windowStart; ms <= endMs; ms += DAY_MS) {
    const key = dayKey(ms).slice(0, 7);
    if (!months.has(key)) months.set(key, { messages: 0, activeDays: 0 });
  }
  for (const day of inWindow) {
    const month = months.get(day.date.slice(0, 7));
    if (!month) continue;
    month.messages += day.count;
    month.activeDays += 1;
  }

  let busiest: DayCount | null = null;
  for (const day of inWindow) if (!busiest || day.count > busiest.count) busiest = day;

  return {
    weeks,
    monthLabels,
    thresholds,
    from: dayKey(windowStart),
    to: dayKey(endMs),
    activeDays: inWindow.length,
    messages: inWindow.reduce((sum, day) => sum + day.count, 0),
    busiest,
    months: [...months].map(([key, value]) => {
      const [year, month] = key.split('-').map(Number);
      return { key, label: `${year}년 ${month}월`, ...value };
    }),
  };
}

const WEEKDAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'] as const;
/** Short chats get bigger squares instead of a mostly empty card. */
const SHORT_HEATMAP_WEEKS = 30;

export function heatmapSummary(model: HeatmapModel): string {
  const range = `${formatDate(dayKeyToMs(model.from))}부터 ${formatDate(dayKeyToMs(model.to))}까지`;
  if (!model.busiest) return `${range} 대화한 날이 없어요.`;
  return `${range} ${formatCount(model.activeDays)}일 대화했어요. 가장 많았던 날은 ${formatDate(dayKeyToMs(model.busiest.date))}, 메시지 ${formatCount(model.busiest.count)}개예요.`;
}

/** Scrollable grid. The month table below is the text alternative. */
export function heatmapMarkup(model: HeatmapModel, idPrefix = 'krc-heatmap'): string {
  const columns = model.weeks.map((week) => `<div class="krc-heat-week">${week.map((cell) => {
    if (cell.future) return '<span class="krc-heat-cell is-future"></span>';
    const state = cell.inRange ? '' : ' is-outside';
    const label = cell.inRange ? `${formatDateWithWeekday(dayKeyToMs(cell.date))} · ${formatCount(cell.count)}개` : '';
    return `<span class="krc-heat-cell${state}" data-level="${cell.level}"${label ? ` title="${escapeHtml(label)}"` : ''}></span>`;
  }).join('')}</div>`).join('');
  const months = model.monthLabels
    .map((month) => `<span style="grid-column:${month.week + 1}">${escapeHtml(month.label)}</span>`)
    .join('');
  const weekdays = WEEKDAY_LABELS.map((label, index) => `<span>${index % 2 === 1 ? label : ''}</span>`).join('');
  const legend = [0, 1, 2, 3, 4].map((level) => `<span class="krc-heat-cell" data-level="${level}"></span>`).join('');
  const rows = model.months.map((month) => `<tr><th scope="row">${escapeHtml(month.label)}</th><td>${formatCount(month.messages)}개</td><td>${formatCount(month.activeDays)}일</td></tr>`).join('');
  const short = model.weeks.length <= SHORT_HEATMAP_WEEKS ? ' is-short' : '';
  return `
    <div class="krc-heatmap${short}">
      <div class="krc-heat-weekdays" aria-hidden="true">${weekdays}</div>
      <div class="krc-heat-scroll" tabindex="0" role="group" aria-label="대화 달력, 가로로 스크롤할 수 있어요" aria-describedby="${idPrefix}-summary">
        <div class="krc-heat-months" aria-hidden="true" style="grid-template-columns:repeat(${model.weeks.length}, var(--krc-heat-step))">${months}</div>
        <div class="krc-heat-grid" aria-hidden="true">${columns}</div>
      </div>
    </div>
    <div class="krc-heat-legend" aria-hidden="true"><span>적음</span>${legend}<span>많음</span></div>
    <p class="krc-chart-summary" id="${idPrefix}-summary">${escapeHtml(heatmapSummary(model))}</p>
    <table class="krc-sr-only"><caption>월별 메시지 수</caption><thead><tr><th scope="col">달</th><th scope="col">메시지</th><th scope="col">대화한 날</th></tr></thead><tbody>${rows}</tbody></table>`;
}
